<template>
  <div>
    <div>
      テスト用のクレジットカード情報
      <table border="1">
        <tr>
          <th>項目</th>
          <th>値</th>
        </tr>
        <tr>
          <td>カード番号</td>
          <td>{{ number }}</td>
        </tr>
        <tr>
          <td>CVC</td>
          <td>{{ cvc }}</td>
        </tr>
        <tr>
          <td>有効期限</td>
          <td>{{ exp_month }}/{{ exp_year }}</td>
        </tr>
        <tr>
          <td>カード名義</td>
          <td>{{ name }}</td>
        </tr>
      </table>
    </div>
    
    <br><br>

    <div>
      <label>カード情報の入力フォーム</label>
      <p>* フォームが表示されない場合、画面をリロードしてください。: <v-btn @click="reload_page">リロードする</v-btn></p>
      <div id="card-element" class="card-element"></div>
    </div>
    
    <br><br>
    
    <div>
      <label>支払い金額: {{ payment_price }}</label>
      <input type="range" v-model="payment_price" min="0" max="10000" step="100" />
      <v-btn @click="pay_handle(payment_price, '')">{{payment_price}}円の支払うテスト</v-btn>
    </div>

    <br><br>

    <div>
      <div v-if="token">トークン: {{ token }}</div>
      <br><br>
      <div v-if="error" style="color:red;">エラー: {{ error }}</div>
    </div>
    
    <br><br>

    <div>
      <div v-if="test_request_body">Test Request Body: {{ test_request_body }}</div>
      <br><br>
      <div v-if="test_response_body">Test Response Body: {{ test_response_body }}</div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'

export default {
  setup() {
    // ######################
    // ライフサイクル
    // ######################
    onMounted(async () => {
      await load_payjp_script();
      generate_form();
    });

    // ######################
    // 実装ドキュメント : https://docs.pay.jp/v1/payjs
    // カード情報
    // ######################
    const payjp = ref(); // Payjpライブラリをグローバル変数から取得
    // カード情報をバインドするための変数
    const number = ref('4242424242424242')
    const cvc = ref('123')
    const exp_month = ref('12')
    const exp_year = ref('30')
    const name = ref('PAY TARO')
    const token = ref('')
    // エラー情報
    const error = ref('')

    // 支払金額
    const payment_price = ref(100); // 支払金額

    // ######################
    // 実装ドキュメント : https://docs.pay.jp/v1/payjs
    // Payjpスクリプトを動的に読み込む
    // ######################
    const script_url = 'https://js.pay.jp/v2/pay.js';
    const load_payjp_script = () => {
      return new Promise((resolve, reject) => {
        if (window.Payjp) return resolve();
        const script = document.createElement('script');
        script.src = script_url;
        script.onload = resolve;
        script.onerror = reject;
        document.body.appendChild(script);
      });
    };

    // ######################
    // フォームの生成
    // ######################
    const card_elements = ref(); // PayjpのElementsを格納する変数
    const pub_key = 'pk_test_d4df6b496b868b45b21f66bf'; // 公開鍵
    const generate_form = () => {
      if (!window.Payjp) {
        error.value = 'Payjpライブラリが読み込まれていません';
        return;
      }
      payjp.value = window.Payjp(pub_key); // 公開鍵
      console.log('Payjpライブラリが読み込まれました。' + payjp.value.version);
      // これで、フォームを生成できる
      card_elements.value = payjp.value.elements()
      const cardElement = card_elements.value.create('card', {style: {base: {color: 'black'}}, disabled: false});
      cardElement.mount('.card-element')
    };

    // ######################
    // 決済処理
    // ######################
    // 支払処理のメインフロー
    const pay_handle = async (price, description) => {
      // 1. トークンを生成
      await create_token();
      // 2. トークンを利用して決済
      await execute_pay(price, description);
    };

    // 1. トークンを生成する関数
    // トークン生成に関する API ドキュメント : https://docs.pay.jp/v1/payjs#create-token
    const create_token = async () => {
      error.value = '';
      token.value = '';
      // 要素ごと送信する
      const cur_card_element = card_elements.value.getElement('card')
      // トークン生成のリクエスト
      await payjp.value.createToken(cur_card_element).then((r) => {
        if (r.error) {
          error.value = r.error.message;
        } else {
          token.value = r.id;
        }
      }).catch((e) => {
        error.value = e.message || 'トークン生成で予期せぬエラー';
      });
    };

    // 2. トークンを利用して決済する
    // 支払いに関する API ドキュメント : https://docs.pay.jp/v1/charge
    // => 支払い処理はサーバーサイドに組み込みます。秘密鍵を使うため。
    const execute_pay = async (price, description) => {
      if (!token.value) {
        error.value = 'トークンが生成されていません';
        return;
      }
      // APIサーバー経由で支払い
      const body = {
        "payment" : {
          card_token: token.value,
          amount: price, // 金額は適宜変更
          currency: 'jpy',
          description: description || '商品説明がありません',
        }
      }
      const res_json = await request_lambda(body)
      test_request_body.value = JSON.stringify(body);
      test_response_body.value = JSON.stringify(res_json);
    };

    const test_request_body = ref("")
    const test_response_body = ref("")

    // #############################
    // サーバーサイドのAPIを呼び出す #
    // #############################
    const request_lambda = async (body) => {
      const body_json = JSON.stringify(body)
      // progress_toast_flag.value = true
      try {
        // 通信
        const res = await fetch('https://6329kl0ydf.execute-api.ap-northeast-1.amazonaws.com/PayJP_v1', {
          method: 'POST',  // APIがPOSTを期待していれば
          headers: {
            'Content-Type': 'application/json'
          },
          body: body_json,
        });

        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }

        const json = await res.json();
        return json;

      } catch (err) {
        alert("[error]\n" + err)
      }
    };


    const reload_page = () => {
      window.location.reload();
    }


    return {
      // フォーム生成
      generate_form,
      // フォームデータ
      name, number, cvc, exp_month, exp_year,
      payment_price,
      // レスポンス
      token, error,
      // 決済処理
      pay_handle,
      create_token, execute_pay,
      // lambdaリクエスト
      request_lambda,
      // リロード
      reload_page,
      // Test
      test_request_body,
      test_response_body,
    }
  }
}
</script>
